import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, Save, X, Download } from 'lucide-react';
import { Markup, CreateMarkupRequest } from '../interfaces/Markup';
import { useMarkup } from '../contexts/MarkupContext';
import MarkupFilters from '../components/MarkupFilters';
import MarkupBulkActions from '../components/MarkupBulkActions';
import { DefaultLoader } from '../components';

const Markups: React.FC = () => {
  const { state, actions } = useMarkup();
  const { loading, error } = state;
  const [showForm, setShowForm] = useState(false);
  const [editingMarkup, setEditingMarkup] = useState<Markup | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  
  const [formData, setFormData] = useState<CreateMarkupRequest>({
    name: '',
    type: 'percentage',
    target: 'adult_price',
    value: 0,
    is_active: true,
    origin_airport: '',
    destination_airport: '',
    airline_name: '',
    days_before_departure: undefined,
  });

  const typeOptions = [
    { value: 'percentage', label: 'Percentual (%)' },
    { value: 'fixed_value', label: 'Valor Fixo (R$)' },
  ];

  const targetOptions = [
    { value: 'adult_price', label: 'Preço Adulto' },
    { value: 'boarding_tax', label: 'Taxa de Embarque' },
    { value: 'total_price', label: 'Preço Total' },
  ];

  useEffect(() => {
    actions.loadMarkups();
  }, []);

  const filteredMarkups = actions.getFilteredMarkups();
  const stats = actions.getMarkupStats();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingMarkup) {
        await actions.updateMarkup(editingMarkup.id, formData);
      } else {
        await actions.createMarkup(formData);
      }
      
      handleCancelForm();
    } catch (err: any) {
    }
  };

  const handleEdit = (markup: Markup) => {
    setEditingMarkup(markup);
    setFormData({
      name: markup.name,
      type: markup.type,
      target: markup.target,
      value: markup.value,
      is_active: markup.is_active,
      origin_airport: markup.origin_airport || '',
      destination_airport: markup.destination_airport || '',
      airline_name: markup.airline_name || '',
      days_before_departure: markup.days_before_departure || undefined,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este markup?')) {
      return;
    }
    
    try {
      await actions.deleteMarkup(id);
    } catch (err: any) {
    }
  };

  const handleExport = () => {
    const csvContent = [
      'Nome,Tipo,Valor,Aplicado em,Origem,Destino,Companhia Aérea,Dias Antes do Embarque,Status,Criado por,Data de Criação',
      ...filteredMarkups.map(markup => [
        markup.name,
        markup.type === 'percentage' ? 'Percentual' : 'Valor Fixo',
        formatValue(markup),
        getTargetLabel(markup.target),
        markup.origin_airport || '',
        markup.destination_airport || '',
        markup.airline_name || '',
        markup.days_before_departure || '',
        markup.is_active ? 'Ativo' : 'Inativo',
        markup.created_by,
        new Date(markup.created_at).toLocaleDateString('pt-BR')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `markups-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingMarkup(null);
    setFormData({
      name: '',
      type: 'percentage',
      target: 'adult_price',
      value: 0,
      is_active: true,
      origin_airport: '',
      destination_airport: '',
      airline_name: '',
      days_before_departure: undefined,
    });
  };

  const formatValue = (markup: Markup) => {
    if (markup.type === 'percentage') {
      return `${markup.value}%`;
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(markup.value);
  };

  const getTargetLabel = (target: string) => {
    const option = targetOptions.find(opt => opt.value === target);
    return option?.label || target;
  };

  if (loading) {
    return <DefaultLoader title="Markups" messages={['Carregando markups...', 'Processando configurações de preço...', 'Organizando dados...']} />;
  }

  return (
    <div>
      <div className="d-flex justify-between align-center mb-4">
        <div>
          <h2 style={{ 
            fontSize: 'var(--font-size-24)', 
            fontWeight: 600, 
            margin: '0 0 8px 0' 
          }}>
            Gestão de Markups
          </h2>
          <p style={{ 
            fontSize: 'var(--font-size-16)', 
            color: 'var(--color-darkgray)', 
            margin: 0 
          }}>
            Configure os markups de preços para passagens aéreas
          </p>
        </div>
        
        <div className="d-flex gap-2">
          <button onClick={handleExport} className="btn btn-secondary" disabled={loading || filteredMarkups.length === 0}>
            <Download size={16} />
            Exportar CSV
          </button>
          <button onClick={actions.loadMarkups} className="btn btn-secondary" disabled={loading}>
            <RefreshCw size={16} />
            Atualizar
          </button>
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            <Plus size={16} />
            Novo Markup
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: 'var(--color-red)',
          padding: '12px 16px',
          borderRadius: 'var(--br-5)',
          marginBottom: '20px',
        }}>
          {error}
        </div>
      )}

      <MarkupFilters />

      <MarkupBulkActions 
        selectedIds={selectedIds} 
        onSelectionChange={setSelectedIds}
      />

      {showForm && (
        <div className="card mb-4">
          <h3 style={{ margin: '0 0 20px 0' }}>
            {editingMarkup ? 'Editar Markup' : 'Novo Markup'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div className="form-group">
                <label className="form-label">Nome do Markup</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  placeholder="Ex: Markup São Paulo"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Tipo</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  className="form-select"
                  required
                >
                  {typeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div className="form-group">
                <label className="form-label">Aplicar em</label>
                <select
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value as any })}
                  className="form-select"
                  required
                >
                  {targetOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Valor {formData.type === 'percentage' ? '(%)' : '(R$)'}
                </label>
                <input
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: Number(e.target.value) })}
                  className="form-input"
                  step={formData.type === 'percentage' ? '0.01' : '0.01'}
                  min="0"
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div className="form-group">
                <label className="form-label">Aeroporto Origem (Opcional)</label>
                <input
                  type="text"
                  value={formData.origin_airport}
                  onChange={(e) => setFormData({ ...formData, origin_airport: e.target.value })}
                  className="form-input"
                  placeholder="Ex: GRU"
                  maxLength={3}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Aeroporto Destino (Opcional)</label>
                <input
                  type="text"
                  value={formData.destination_airport}
                  onChange={(e) => setFormData({ ...formData, destination_airport: e.target.value })}
                  className="form-input"
                  placeholder="Ex: SDU"
                  maxLength={3}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div className="form-group">
                <label className="form-label">Companhia Aérea (Opcional)</label>
                <input
                  type="text"
                  value={formData.airline_name}
                  onChange={(e) => setFormData({ ...formData, airline_name: e.target.value })}
                  className="form-input"
                  placeholder="Ex: GOL, AZUL, LATAM"
                  style={{ textTransform: 'uppercase' }}
                />
                <small style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginTop: '4px' }}>
                  Aplicar apenas a voos desta companhia aérea
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Dias Antes do Embarque (Opcional)</label>
                <input
                  type="number"
                  value={formData.days_before_departure || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    days_before_departure: e.target.value ? Number(e.target.value) : undefined 
                  })}
                  className="form-input"
                  placeholder="Ex: 5"
                  min="0"
                  max="365"
                />
                <small style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginTop: '4px' }}>
                  Aplicar apenas se embarque for em menos de X dias
                </small>
              </div>
            </div>

            <div className="form-group mb-3">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Markup ativo
              </label>
            </div>

            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                <Save size={16} />
                Salvar
              </button>
              <button type="button" onClick={handleCancelForm} className="btn btn-secondary">
                <X size={16} />
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === filteredMarkups.length && filteredMarkups.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(filteredMarkups.map(m => m.id));
                    } else {
                      setSelectedIds([]);
                    }
                  }}
                />
              </th>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Valor</th>
              <th>Aplicado em</th>
              <th>Rota</th>
              <th>Companhia</th>
              <th>Dias Embarque</th>
              <th>Status</th>
              <th>Criado por</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredMarkups.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-center" style={{ padding: '40px' }}>
                  {state.filters.search || state.filters.status !== 'all' || state.filters.type !== 'all' || state.filters.target !== 'all'
                    ? 'Nenhum markup encontrado com os filtros aplicados'
                    : 'Nenhum markup cadastrado'}
                </td>
              </tr>
            ) : (
              filteredMarkups.map((markup) => (
                <tr key={markup.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(markup.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds([...selectedIds, markup.id]);
                        } else {
                          setSelectedIds(selectedIds.filter(id => id !== markup.id));
                        }
                      }}
                    />
                  </td>
                  <td style={{ fontWeight: 500 }}>{markup.name}</td>
                  <td>
                    {markup.type === 'percentage' ? 'Percentual' : 'Valor Fixo'}
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    {formatValue(markup)}
                  </td>
                  <td>{getTargetLabel(markup.target)}</td>
                  <td>
                    {markup.origin_airport || markup.destination_airport ? (
                      <span style={{ fontFamily: 'monospace' }}>
                        {markup.origin_airport || 'XXX'} → {markup.destination_airport || 'XXX'}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-darkgray)', fontStyle: 'italic' }}>
                        Global
                      </span>
                    )}
                  </td>
                  <td>
                    {markup.airline_name ? (
                      <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {markup.airline_name}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-darkgray)', fontStyle: 'italic' }}>
                        Todas
                      </span>
                    )}
                  </td>
                  <td>
                    {markup.days_before_departure !== null && markup.days_before_departure !== undefined ? (
                      <span>
                        {'<'} {markup.days_before_departure} dias
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-darkgray)', fontStyle: 'italic' }}>
                        Sempre
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${markup.is_active ? 'badge-completed' : 'badge-cancelled'}`}>
                      {markup.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td style={{ fontSize: 'var(--font-size-12)' }}>
                    {markup.created_by}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(markup)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px' }}
                        title="Editar"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(markup.id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 12px' }}
                        title="Excluir"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
        marginTop: '24px'
      }}>
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-primary)' }}>
            {stats.total}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Total</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-green)' }}>
            {stats.active}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Ativos</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-red)' }}>
            {stats.inactive}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Inativos</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-blue)' }}>
            {stats.percentage}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Percentuais</p>
        </div>
        
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-orange)' }}>
            {stats.fixedValue}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Valor Fixo</p>
        </div>
      </div>
    </div>
  );
};

export default Markups;